import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import Avatar from '../../components/Avatar';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_LABELS = {
  pending: 'pending to solve',
  inProgress: 'in progress',
  solved: 'solved',
};

const STATUS_OPTIONS = ['pending to solve', 'in progress', 'solved'];

const statusKey = (status) => {
  if (status === 'pending to solve') return 'pending';
  if (status === 'in progress') return 'inProgress';
  return 'solved';
};

const computeStats = (list) => {
  const base = { total: 0, pending: 0, inProgress: 0, solved: 0 };
  return list.reduce((acc, item) => {
    const key = statusKey(item.status);
    acc.total += 1;
    acc[key] += 1;
    return acc;
  }, { ...base });
};

export default function Problems() {
  const [problems, setProblems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, inProgress: 0, solved: 0 });
  const [filterStatus, setFilterStatus] = useState('pending to solve');
  const [searchText, setSearchText] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [newProblemDescription, setNewProblemDescription] = useState('');
  const [newProblemStatus, setNewProblemStatus] = useState('pending to solve');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('pending to solve');
  const [newComment, setNewComment] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [statusModalProblem, setStatusModalProblem] = useState(null);
  const [statusModalValue, setStatusModalValue] = useState('pending to solve');
  const [statusModalBusy, setStatusModalBusy] = useState(false);
  const [commentsModalProblem, setCommentsModalProblem] = useState(null);
  const [commentActionId, setCommentActionId] = useState(null);

  const filteredProblems = useMemo(() => {
    let list = filterStatus === 'all' ? problems : problems.filter((p) => p.status === filterStatus);
    const term = searchText.trim().toLowerCase();
    if (term) {
      list = list.filter((p) => (
        (p.description || '').toLowerCase().includes(term)
        || (p.creator?.name || '').toLowerCase().includes(term)
      ));
    }
    return list;
  }, [filterStatus, searchText, problems]);

  // Exports exactly the current view -- same status filter and search text
  // already applied to `filteredProblems` -- so there's nothing extra to
  // recompute here.
  const handleExportExcel = () => {
    const headers = ['ID', 'Description', 'Status', 'Created By', 'Created At', 'Last Updated By', 'Last Updated At', 'Comments', 'Comment Log'];
    const rows = filteredProblems.map((p) => [
      p.problemId,
      p.description || '',
      p.status || '',
      p.creator?.name || '',
      formatDate(p.createdAt),
      p.updater?.name || '',
      formatDate(p.updatedAt),
      p.comments?.length || 0,
      [...(p.comments || [])]
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .map((c) => `[${formatDate(c.createdAt)}] ${c.creator?.name || 'Unknown'}: ${c.comment}`)
        .join('\n'),
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet['!cols'] = [{ wch: 8 }, { wch: 50 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 60 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Problems');
    const statusLabel = filterStatus === 'all' ? 'All' : (STATUS_LABELS[statusKey(filterStatus)] || filterStatus).replace(/\s+/g, '_');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Problems_${statusLabel}_${stamp}.xlsx`);
  };

  useEffect(() => {
    const load = async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const resp = await axiosInstance.get('/admin/problems');
        const list = resp?.data?.problems || [];
        setProblems(list);
        setStats(computeStats(list));
      } catch (err) {
        console.error('Failed to load problems', err);
        setError(err?.response?.data?.message || 'Failed to load problems');
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, []);

  const openStatusModal = (problem) => {
    setStatusModalProblem(problem);
    setStatusModalValue(problem.status || 'pending to solve');
  };

  const closeStatusModal = () => {
    setStatusModalProblem(null);
    setStatusModalValue('pending to solve');
  };

  const openCommentsModal = (problem) => {
    setCommentsModalProblem(problem);
    setNewComment('');
  };

  const closeCommentsModal = () => {
    setCommentsModalProblem(null);
    setNewComment('');
    setCommentActionId(null);
  };

  const handleAddProblem = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.post('/admin/problems', {
        description: newProblemDescription,
        status: newProblemStatus,
      });
      const created = resp?.data?.problem;
      if (created) {
        const next = [created, ...problems];
        setProblems(next);
        setStats(computeStats(next));
        setIsAddModalOpen(false);
        setNewProblemDescription('');
        setNewProblemStatus('pending to solve');
      }
    } catch (err) {
      console.error('Failed to add problem', err);
      setError(err?.response?.data?.message || 'Failed to add problem');
    } finally {
      setBusy(false);
    }
  };

  const openEditModal = (problem) => {
    setSelectedProblem(problem);
    setEditDescription(problem.description);
    setEditStatus(problem.status);
    setIsEditModalOpen(true);
  };

  const handleEditProblem = async (event) => {
    event.preventDefault();
    if (!selectedProblem) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.put(`/admin/problems/${selectedProblem.problemId}`, {
        description: editDescription,
        status: editStatus,
      });
      const updated = resp?.data?.problem;
      if (updated) {
        const next = problems.map((p) => (p.problemId === updated.problemId ? updated : p));
        setProblems(next);
        setStats(computeStats(next));
        setIsEditModalOpen(false);
        setSelectedProblem(null);
      }
    } catch (err) {
      console.error('Failed to update problem', err);
      setError(err?.response?.data?.message || 'Failed to update problem');
    } finally {
      setBusy(false);
    }
  };

  const handleStatusSubmit = async (event) => {
    event.preventDefault();
    if (!statusModalProblem) return;
    if (statusModalValue === statusModalProblem.status) {
      closeStatusModal();
      return;
    }
    setStatusModalBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.put(`/admin/problems/${statusModalProblem.problemId}`, {
        description: statusModalProblem.description,
        status: statusModalValue,
      });
      const updated = resp?.data?.problem;
      if (updated) {
        const next = problems.map((p) => (p.problemId === updated.problemId ? updated : p));
        setProblems(next);
        setStats(computeStats(next));
        closeStatusModal();
      }
    } catch (err) {
      console.error('Failed to change status', err);
      setError(err?.response?.data?.message || 'Failed to change status');
    } finally {
      setStatusModalBusy(false);
    }
  };

  const handleDeleteProblem = async (problemId) => {
    const { isConfirmed } = await Swal.fire({
      title: 'Remove problem record?',
      text: 'Deleting will remove this problem and all associated comments.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Keep it',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2 font-semibold',
        cancelButton: 'rounded-lg px-6 py-2 font-semibold',
      },
    });
    if (!isConfirmed) return;
    setBusy(true);
    setError(null);
    try {
      await axiosInstance.delete(`/admin/problems/${problemId}`);
      const next = problems.filter((p) => p.problemId !== problemId);
      setProblems(next);
      setStats(computeStats(next));
    } catch (err) {
      console.error('Failed to delete problem', err);
      setError(err?.response?.data?.message || 'Failed to delete problem');
    } finally {
      setBusy(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentsModalProblem || !newComment.trim()) return;
    const problemId = commentsModalProblem.problemId;
    setBusy(true);
    setError(null);
    try {
      const resp = await axiosInstance.post(`/admin/problems/${problemId}/comments`, {
        comment: newComment,
      });
      const created = resp?.data?.comment;
      if (created) {
        const next = problems.map((p) => {
          if (p.problemId !== problemId) return p;
          const comments = p.comments ? [created, ...p.comments] : [created];
          return { ...p, comments };
        });
        setProblems(next);
        setNewComment('');
        const refreshed = next.find((p) => p.problemId === problemId);
        setCommentsModalProblem(refreshed || null);
      }
    } catch (err) {
      console.error('Failed to add comment', err);
      setError(err?.response?.data?.message || 'Failed to add comment');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentsModalProblem) return;
    const { isConfirmed } = await Swal.fire({
      title: 'Delete this comment?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'rounded-lg px-6 py-2 font-semibold',
        cancelButton: 'rounded-lg px-6 py-2 font-semibold',
      },
    });
    if (!isConfirmed) return;
    setCommentActionId(commentId);
    setError(null);
    try {
      await axiosInstance.delete(`/admin/problems/${commentsModalProblem.problemId}/comments/${commentId}`);
      const next = problems.map((p) => {
        if (p.problemId !== commentsModalProblem.problemId) return p;
        const comments = (p.comments || []).filter((c) => c.commentId !== commentId);
        return { ...p, comments };
      });
      setProblems(next);
      const refreshed = next.find((p) => p.problemId === commentsModalProblem.problemId);
      setCommentsModalProblem(refreshed || null);
    } catch (err) {
      console.error('Failed to delete comment', err);
      setError(err?.response?.data?.message || 'Failed to delete comment');
    } finally {
      setCommentActionId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'pending to solve') return 'bg-red-100 text-red-800 border-red-300';
    if (status === 'in progress') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (status === 'solved') return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout title="Issues & Problems">
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Issues &amp; Problems</h1>
          <p className="mt-2 text-gray-600">Track and manage issues reported by staff</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={filteredProblems.length === 0}
            title="Export the problems matching the current filter and search"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow transition-colors hover:bg-indigo-700"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
            Add New Problem
          </button>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { key: 'all', label: 'Total Problems', value: stats.total, icon: 'M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15.5A2.25 2.25 0 0022.25 18v-4.162c0-.224-.034-.447-.1-.661L19.738 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859M12 3v8.25m0 0l-3-3m3 3l3-3', ring: 'ring-indigo-100', text: 'text-gray-900', iconWrap: 'bg-indigo-50 text-indigo-600' },
          { key: 'pending to solve', label: 'Pending', value: stats.pending, icon: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z', ring: 'ring-red-100', text: 'text-red-600', iconWrap: 'bg-red-50 text-red-600' },
          { key: 'in progress', label: 'In Progress', value: stats.inProgress, icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', ring: 'ring-yellow-100', text: 'text-yellow-600', iconWrap: 'bg-yellow-50 text-yellow-600' },
          { key: 'solved', label: 'Solved', value: stats.solved, icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z', ring: 'ring-green-100', text: 'text-green-600', iconWrap: 'bg-green-50 text-green-600' },
        ].map((card) => (
          <button
            key={card.key}
            type="button"
            onClick={() => setFilterStatus(card.key)}
            className={`rounded-xl border bg-white p-5 text-left shadow-sm transition hover:shadow-md ${
              filterStatus === card.key ? `border-transparent ring-2 ${card.ring}` : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-500">{card.label}</p>
              <span className={`flex h-9 w-9 items-center justify-center rounded-full ${card.iconWrap}`}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={card.icon} /></svg>
              </span>
            </div>
            <p className={`mt-2 text-3xl font-bold ${card.text}`}>{card.value}</p>
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-1">
          {['pending to solve', 'in progress', 'solved', 'all'].map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFilterStatus(status)}
              className={`rounded-md px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                filterStatus === status
                  ? status === 'pending to solve'
                    ? 'bg-red-600 text-white'
                    : status === 'in progress'
                      ? 'bg-yellow-600 text-white'
                      : status === 'solved'
                        ? 'bg-green-600 text-white'
                        : 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {status === 'all' ? 'All' : STATUS_LABELS[statusKey(status)] ?? status}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search description or reporter…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {initialLoading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-400">
          <svg className="mx-auto h-6 w-6 animate-spin text-gray-300" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
          <p className="mt-3">Loading problems…</p>
        </div>
      ) : filteredProblems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          <p className="mt-3 text-sm text-gray-500">
            {problems.length === 0 ? 'No problems found. Create the first one.' : 'Nothing matches the current filter/search.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredProblems.map((problem) => (
            <div
              key={problem.problemId}
              className="rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-indigo-200 hover:shadow-md"
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => openStatusModal(problem)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${getStatusBadgeClass(problem.status)} hover:shadow`}
                        title="Update status"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {problem.status || 'pending to solve'}
                      </button>
                      <span className="text-xs text-gray-400">Created {formatDate(problem.createdAt)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{problem.description}</p>
                    {problem.creator?.name && (
                      <div className="flex items-center gap-2 pt-1">
                        <Avatar name={problem.creator.name} imagePath={problem.creator.imagePath} size="h-5 w-5" />
                        <span className="text-xs font-medium text-gray-500">{problem.creator.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(problem)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                      title="Edit problem"
                      aria-label="Edit problem"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteProblem(problem.problemId)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete problem"
                      aria-label="Delete problem"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
                  <button
                    type="button"
                    onClick={() => openCommentsModal(problem)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                    {(problem.comments?.length || 0)} {problem.comments?.length === 1 ? 'Comment' : 'Comments'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {statusModalProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Update status</p>
              <p className="whitespace-pre-wrap text-lg font-semibold leading-snug text-gray-900">{statusModalProblem.description}</p>
              <p className="mt-2 text-sm text-gray-500">Created {formatDate(statusModalProblem.createdAt)}</p>
            </div>
            <form onSubmit={handleStatusSubmit} className="space-y-4">
              <div className="space-y-3">
                {STATUS_OPTIONS.map((option) => (
                  <label
                    key={option}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-4 transition ${
                      statusModalValue === option ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={option}
                      checked={statusModalValue === option}
                      onChange={(e) => setStatusModalValue(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-semibold capitalize text-gray-900">{option}</p>
                      <p className="text-xs text-gray-500">
                        {option === 'pending to solve' && 'Awaiting assignment or clarification.'}
                        {option === 'in progress' && 'Owner acknowledged and actively working.'}
                        {option === 'solved' && 'Issue verified, communication sent.'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeStatusModal}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={statusModalBusy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {statusModalBusy ? 'Saving...' : 'Confirm status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {commentsModalProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Comments</p>
                <p className="whitespace-pre-wrap text-lg font-semibold leading-snug text-gray-900">{commentsModalProblem.description}</p>
                <p className="mt-2 text-sm text-gray-500">Created {formatDate(commentsModalProblem.createdAt)}</p>
              </div>
              <button
                type="button"
                onClick={closeCommentsModal}
                className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
                title="Close"
                aria-label="Close"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Add a new comment</label>
              <textarea
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                placeholder="Document your update… (paragraphs are kept as you paste them)"
                className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={4}
                style={{ minHeight: '6rem', maxHeight: '20rem', overflowY: 'auto' }}
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || busy}
                  className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Posting...' : 'Post comment'}
                </button>
              </div>
            </div>

            <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-gray-400">
              <span>Log history</span>
              <span>Most recent first</span>
            </div>
            <div className="max-h-80 space-y-4 overflow-y-auto pr-2">
              {(commentsModalProblem.comments && commentsModalProblem.comments.length > 0)
                ? [...commentsModalProblem.comments]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .map((comment) => (
                      <div key={comment.commentId} className="rounded-2xl border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={comment.creator?.name} imagePath={comment.creator?.imagePath} size="h-8 w-8" />
                            <div>
                              <p className="font-semibold text-gray-900">{comment.creator?.name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500">{formatDate(comment.createdAt)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteComment(comment.commentId)}
                            disabled={commentActionId === comment.commentId}
                            className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                          >
                            {commentActionId === comment.commentId ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                        <p className="mt-3 whitespace-pre-wrap text-gray-700 leading-relaxed">{comment.comment}</p>
                      </div>
                    ))
                : (
                  <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
                    No comments logged yet.
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Add New Problem</h2>
            <form onSubmit={handleAddProblem}>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={newProblemDescription}
                  onChange={(e) => {
                    setNewProblemDescription(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                  placeholder="Describe the problem… (paragraphs are kept as you paste them)"
                  style={{ minHeight: '7rem', maxHeight: '24rem', overflowY: 'auto' }}
                />
              </div>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={newProblemStatus}
                  onChange={(e) => setNewProblemStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="pending to solve">Pending to Solve</option>
                  <option value="in progress">In Progress</option>
                  <option value="solved">Solved</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Adding...' : 'Add Problem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditModalOpen && selectedProblem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Edit Problem</h2>
            <form onSubmit={handleEditProblem}>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  className="w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={5}
                  required
                  style={{ minHeight: '7rem', maxHeight: '24rem', overflowY: 'auto' }}
                />
              </div>
              <div className="mb-6">
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="pending to solve">Pending to Solve</option>
                  <option value="in progress">In Progress</option>
                  <option value="solved">Solved</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedProblem(null);
                  }}
                  className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-indigo-600 px-6 py-2.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
